import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Input,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import { ErrorMessage, Form, Formik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { TagsInput } from "react-tag-input-component";
import {
  CatalogProductStatus,
  colors,
  Product,
  ProductInitialValues,
  ProductValidationSchema,
} from "../models/features/Product/Product";
import {
  useGetCategoriesQuery,
  useGetManufacturersQuery,
  useCreateProductMutation,
} from "../redux/reducer/features/Product/productSlice";
import LenghtUnitList from "../compenents/app/LenghtUnitList";
import MassUnitList from "../compenents/app/MassUnitList";
import PageLoader from "../compenents/app/PageLoader";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FileUploader } from "react-drag-drop-files";
import CloseIcon from "@mui/icons-material/Close";
import Lightbox from "react-image-lightbox";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
export interface AddProductProps {}

export const AddProduct: React.FunctionComponent<AddProductProps> = () => {
  const { data, isLoading } = useGetCategoriesQuery();
  const [createProductMutation, createProductMutationResult] =
    useCreateProductMutation();
  const navigate = useNavigate();
  const [images, setImages] = useState<
    { image: File; title: string; description: string }[]
  >([]);
  const [imageMeta, setImageMeta] = useState({
    index: 0,
    title: "",
    description: "",
  });
  const manufacturers = useGetManufacturersQuery();
  const [errorMessage, setErrorMessage] = React.useState("");
  const [year, setYear] = React.useState<Dayjs | null>();
  const [openImageDialog, setOpenImageDialog] = React.useState<boolean>(false);
  const [lightBoxURL, setLightBoxURL] = React.useState("");
  const [isLightBoxOpen, setLightBoxOpen] = React.useState(false);
  const [formikInitialValues, setFormikInititalValues] =
    React.useState<Product>(ProductInitialValues);

  let defaultColors =
    formikInitialValues?.colors?.map((i) => {
      return i.title;
    }) ?? [];

  const categoryDefaultProps = {
    options:
      data && data.entityList
        ? data.entityList.filter((n) => !n.isSubCategory)
        : [],
    getOptionLabel: (option: any) => option.title,
  };

  const manufacturerDefaultProps = {
    options:
      manufacturers.data && manufacturers.data.entityList
        ? manufacturers.data.entityList
        : [],
    getOptionLabel: (option: any) => option.title,
  };

  const handleOnSubmit = (values: Product) => {
    setErrorMessage("");
    const formData = new FormData();
    values.id = undefined;
    formData.append("productJSON", JSON.stringify(values));
    images.forEach((imageWithTitle, index) => {
      if (imageWithTitle.image) {
        formData.append(`images[${index}].file`, imageWithTitle.image);
        formData.append(`images[${index}].title`, imageWithTitle.title);
        formData.append(
          `images[${index}].description`,
          imageWithTitle.description
        );
      }
    });

    createProductMutation({ data: formData }).then((res: any) => {
      if (res.data.status === 200) {
        navigate("/Products");
      } else if (res.data.status === 409) {
        setErrorMessage(
          "This product already exists with same Model and Manufacturer*"
        );
      } else {
        setErrorMessage("Sorry we found an error while creating product.");
      }
    });
  };
  const handleSaveImageMeta = () => {
    setImages(
      images.map((img, index: number) =>
        index === imageMeta.index
          ? {
              ...img,
              title: imageMeta.title,
              description: imageMeta.description,
            }
          : img
      )
    );
    setOpenImageDialog(false);
  };
  const handleClose = () => {
    setLightBoxOpen(false);
    setOpenImageDialog(false);
  };
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const updatedImages = Array.from(images);
    const [reorderedItem] = updatedImages.splice(result.source.index, 1);
    updatedImages.splice(result.destination.index, 0, reorderedItem);
    setImages(updatedImages);
  };
  return (
    <>
      <PageLoader isLoading={false} />
      <Container maxWidth={false}>
        <Card style={{ padding: "10px", margin: "10px" }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to="/">
              Dashboard
            </Link>
            <Link color="inherit" to="/Products">
              Products
            </Link>
            <Typography color="text.primary">Add Product</Typography>
          </Breadcrumbs>
        </Card>
        <Card style={{ padding: "10px", margin: "10px" }}>
          <Formik
            initialValues={formikInitialValues}
            validationSchema={ProductValidationSchema}
            enableReinitialize
            onSubmit={(values) => {
              handleOnSubmit(values);
            }}
          >
            {({ values, errors, handleChange, handleBlur, setFieldValue }) => (
              <>
                <Form>
                  <Grid container spacing={3} p={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        {...manufacturerDefaultProps}
                        id="clear-on-escape"
                        clearOnEscape
                        loading={isLoading}
                        defaultValue={formikInitialValues.productManufacturer}
                        onChange={(e, value) => {
                          setFieldValue(
                            "manufacturerId",
                            value ? value.id : ""
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Manufacturer"
                            variant="standard"
                          />
                        )}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="manufacturerId"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        id="modelNumber"
                        name="modelNumber"
                        label="Model Number"
                        fullWidth
                        autoComplete="shipping address-line2"
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.modelNumber}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="modelNumber"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        id="modelName"
                        name="modelName"
                        label="Model Name"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.modelName}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="modelName"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        id="title"
                        name="title"
                        label="Display Title"
                        fullWidth
                        autoComplete="family-name"
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.title}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="title"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        {...categoryDefaultProps}
                        id="clear-on-escape"
                        loading={isLoading}
                        clearOnEscape
                        defaultValue={formikInitialValues.category}
                        onChange={(e, value) => {
                          setFieldValue(
                            "productCategoryId",
                            value ? value.id : ""
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category"
                            variant="standard"
                          />
                        )}
                      />
                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="productCategoryId"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        id="sku"
                        name="sku"
                        label="SKU"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.sku}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          views={["year"]}
                          label=" Year"
                          value={year?.toString() ?? null}
                          onChange={(newValue) => {
                            var date = new Date(newValue?.toString() ?? "");
                            setYear(dayjs(newValue));
                            setFieldValue(
                              "year",
                              date.getFullYear().toString()
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              helperText={null}
                              fullWidth
                              variant="standard"
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="powerType"
                        name="powerType"
                        label="PowerType"
                        fullWidth
                        autoComplete="shipping country"
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.powerType}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        multiple
                        options={colors}
                        defaultValue={[...defaultColors]}
                        id="clear-on-escape"
                        clearOnEscape
                        onChange={(e, value) => {
                          setFieldValue(
                            "colors",
                            value.map((item) => ({
                              title: item,
                              shortTitle: item,
                            }))
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Colors"
                            variant="standard"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="upc"
                        name="upc"
                        label="UPC"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.upc}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputLabel htmlFor="dimension.depth" shrink>
                        Length
                      </InputLabel>
                      <Input
                        id="dimension.length"
                        name="dimension.length"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.dimension?.length}
                        endAdornment={
                          <LenghtUnitList
                            value={values.dimension?.lengthUnit}
                            id={"dimension.lengthUnit"}
                            name={"dimension.lengthUnit"}
                            onChange={handleChange}
                          ></LenghtUnitList>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputLabel htmlFor="dimension.height" shrink>
                        Height
                      </InputLabel>
                      <Input
                        id="dimension.height"
                        name="dimension.height"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.dimension?.height}
                        endAdornment={
                          <LenghtUnitList
                            value={values.dimension?.heightUnit}
                            id={"dimension.heightUnit"}
                            name={"dimension.heightUnit"}
                            onChange={handleChange}
                          ></LenghtUnitList>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputLabel htmlFor="dimension.weight" shrink>
                        Weight
                      </InputLabel>
                      <Input
                        id="dimension.weight"
                        name="dimension.weight"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.dimension?.weight}
                        endAdornment={
                          <MassUnitList
                            value={values.dimension?.weightUnit}
                            id={"dimension.weightUnit"}
                            name={"dimension.weightUnit"}
                            onChange={handleChange}
                          ></MassUnitList>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputLabel htmlFor="dimension.width" shrink>
                        width
                      </InputLabel>
                      <Input
                        id="dimension.width"
                        name="dimension.width"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.dimension?.width}
                        endAdornment={
                          <LenghtUnitList
                            value={values.dimension?.widthUnit}
                            id={"dimension.widthUnit"}
                            name={"dimension.widthUnit"}
                            onChange={handleChange}
                          ></LenghtUnitList>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TagsInput
                        value={values.tags}
                        onChange={(tags) => {
                          setFieldValue("tags", tags);
                        }}
                        name="Tags"
                        placeHolder="enter tags"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <ReactQuill
                        id={"description"}
                        value={values.description}
                        placeholder="Description"
                        theme="snow"
                        modules={{
                          clipboard: {
                            matchVisual: false,
                          },
                        }}
                        onChange={(e) => {
                          setFieldValue("description", e);
                        }}
                      />

                      <ErrorMessage
                        className="error-msg"
                        component={"div"}
                        name="description"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <FileUploader
                        handleChange={(e: any) => {
                          const files = Array.from(e as FileList);
                          const newImages = files.map((file) => ({
                            image: file,
                            title: "",
                            description: "",
                          }));
                          setImages([...images, ...newImages]);
                        }}
                        maxSize={20}
                        multiple
                        name="images"
                        types={["JPG", "JPEG", "PNG", "WebP"]}
                      />
                    </Grid>
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="images" direction="horizontal">
                        {(provided) => (
                          <Grid
                            container
                            item
                            xs={12}
                            display="flex"
                            alignItems="center"
                            gap={2}
                            flexWrap="wrap"
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {images.map((file, index) => (
                              <Draggable
                                key={index}
                                draggableId={`image-${index}`}
                                index={index}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={{
                                      position: "relative",
                                      display: "inline-block",
                                      border: "1px solid black",
                                      width: "150px",
                                      height: "100px",
                                      borderRadius: "10px",
                                      ...provided.draggableProps.style,
                                    }}
                                  >
                                    <img
                                      style={{
                                        height: "100%",
                                        width: "100%",
                                        borderRadius: "10px",
                                      }}
                                      alt="preview image"
                                      onClick={() => {
                                        setLightBoxOpen(true);
                                        setLightBoxURL(
                                          URL.createObjectURL(file.image)
                                        );
                                      }}
                                      src={URL.createObjectURL(file.image)}
                                    />
                                    <IconButton
                                      size="small"
                                      style={{
                                        position: "absolute",
                                        top: 1,
                                        right: 30,
                                        backgroundColor: "white",
                                      }}
                                      onClick={() => {
                                        setImageMeta({
                                          index: index,
                                          title: file.title,
                                          description: file.description,
                                        });
                                        setOpenImageDialog(true);
                                      }}
                                    >
                                      <MoreHorizIcon
                                        sx={{
                                          color: "black",
                                          fontSize: "13px",
                                        }}
                                      />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      style={{
                                        position: "absolute",
                                        top: 1,
                                        right: 1,
                                        backgroundColor: "white",
                                      }}
                                      onClick={() => {
                                        setImages(
                                          images.filter(
                                            (img, imgIndex) =>
                                              imgIndex !== index
                                          )
                                        );
                                      }}
                                    >
                                      <CloseIcon
                                        color="error"
                                        sx={{
                                          fontSize: "13px",
                                        }}
                                      />
                                    </IconButton>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Grid>
                        )}
                      </Droppable>
                    </DragDropContext>

                    <Grid
                      display={"flex"}
                      alignItems={"center"}
                      gap={3}
                      item
                      xs={12}
                    >
                      <Button
                        variant="contained"
                        type="submit"
                        onClick={() =>
                          setFieldValue("status", CatalogProductStatus.Active)
                        }
                        endIcon={
                          createProductMutationResult.isLoading &&
                          values.status === CatalogProductStatus.Active ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : (
                            <></>
                          )
                        }
                        disabled={createProductMutationResult.isLoading}
                      >
                        Save & Add
                      </Button>
                      <Button
                        variant="contained"
                        type="submit"
                        onClick={() =>
                          setFieldValue("status", CatalogProductStatus.Draft)
                        }
                        endIcon={
                          createProductMutationResult.isLoading &&
                          values.status === CatalogProductStatus.Draft ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : (
                            <></>
                          )
                        }
                        disabled={createProductMutationResult.isLoading}
                      >
                        Save As Draft
                      </Button>
                      <Typography className="error-msg">
                        {errorMessage}
                      </Typography>
                    </Grid>
                  </Grid>
                </Form>
              </>
            )}
          </Formik>
        </Card>
      </Container>
      <Dialog
        onClose={handleClose}
        maxWidth={"sm"}
        fullWidth
        open={openImageDialog}
      >
        <DialogTitle>Image Data</DialogTitle>
        <DialogContent>
          <Grid item xs={12}>
            <TextField
              id="title"
              name="title"
              label="Title"
              fullWidth
              value={imageMeta.title}
              onChange={(e) => {
                setImageMeta({ ...imageMeta, title: e.target.value });
              }}
              variant="standard"
            />
          </Grid>
          <Grid mt={1} item xs={12}>
            <TextField
              id="description"
              name="description"
              label="Description"
              fullWidth
              variant="standard"
              value={imageMeta.description}
              onChange={(e) => {
                setImageMeta({ ...imageMeta, description: e.target.value });
              }}
            />
          </Grid>
          <Box mt={3} display={"flex"} justifyContent={"flex-end"} gap={2}>
            <Button variant="outlined" size="small" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveImageMeta}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {isLightBoxOpen && (
        <Lightbox mainSrc={lightBoxURL} onCloseRequest={handleClose} />
      )}
    </>
  );
};
